import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1595856898575-9d187bd32fd6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Hero = () => (
  <div className="relative hidden overflow-hidden lg:block">
    <img src={HERO} alt="Tehnician service" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/20" />
    <div className="relative flex h-full flex-col justify-end p-12 text-white">
      <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-green-400/40 bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-300 backdrop-blur">
        <MessageCircle className="h-3.5 w-3.5" /> Import direct din grupul WhatsApp „service”
      </div>
      <h2 className="max-w-md text-4xl font-extrabold leading-tight tracking-tight">
        Lipești mesajul. AI-ul face tichetul.
      </h2>
      <p className="mt-3 max-w-md text-slate-300">
        Client, telefon, adresă, echipament și problema — extrase automat în câteva secunde.
      </p>
    </div>
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      <Hero />
      <div className="flex items-center justify-center bg-grid px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="login-form">
          <img src="/logo.png" alt="Drag de Lemn" className="h-24 w-24 object-contain" data-testid="login-logo" />
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">DDL Hub</h1>
            <p className="mt-1 text-sm text-muted-foreground">Autentifică-te pentru a gestiona tichetele de service.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Parolă</Label>
            <div className="relative">
              <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11 pr-11" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Ascunde parola" : "Arată parola"} data-testid="login-toggle-password"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-11 w-full text-base font-semibold" data-testid="login-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Intră în cont
          </Button>
          <p className="text-xs text-muted-foreground">Conturile tehnicienilor sunt create de administrator din secțiunea Echipă.</p>
        </form>
      </div>
    </div>
  );
}
